import { useMutation, useQueryClient } from '@tanstack/react-query'
import { cancelAction, partyActionKeys } from '@/services/partyActions'
import { actionPointKeys } from '@/services/actionPoints'

/**
 * Hook to cancel a queued party action.
 *
 * Cancels the action and invalidates both the queued actions and
 * action points queries to refresh the UI.
 *
 * @param partyId - The party that owns the action
 * @param currentTick - The current game tick
 * @returns Object with cancel function, pending state, and error
 */
export function useCancelAction(
  partyId: string | undefined,
  currentTick: number | undefined
) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (actionId: string) => cancelAction(actionId),
    onSuccess: () => {
      // Invalidate queued actions to refresh the list
      if (partyId && currentTick !== undefined) {
        queryClient.invalidateQueries({
          queryKey: partyActionKeys.queued(partyId, currentTick),
        })
        // Also invalidate action points since spent AP will decrease
        queryClient.invalidateQueries({
          queryKey: actionPointKeys.byPartyTick(partyId, currentTick),
        })
      }
    },
  })

  return {
    cancelAction: mutation.mutate,
    cancelActionAsync: mutation.mutateAsync,
    isCancelling: mutation.isPending,
    error: mutation.error,
  }
}
